import {AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, inject, model, signal, viewChild} from '@angular/core';
import {WjNavModule} from '@mescius/wijmo.angular2.nav';
import {WjInputModule} from '@mescius/wijmo.angular2.input';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {ApiConfiguration} from '../../restclient/api.config';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {AppDevToolsService} from '../../service/app-devtools.service';
import {AppRoleAndPermissions} from '../../security/app-role-and-permissions';
import {WjGridFilterModule} from '@mescius/wijmo.angular2.grid.filter';
import {NgDynamicComponentsService} from '../../service/ng-dynamic-components.service';
import {DevToolsFormulas} from './app-developer-tools-formula';
import {WjGridSearchModule} from '@mescius/wijmo.angular2.grid.search';
import {RouterLink} from '@angular/router';
import {ApiEnvironment, AppConstants} from '../../app.constants';
import {MwcCheckboxControlValueAccessorDirective} from '../../directives/mwc-checkbox-control-value-accessor.directive';
import {PortfolioDataContext} from '../../data/portfolio-data-context.service';
import {AdminUserPermissionsComponent} from '../../components/admin-user-permissions/admin-user-permissions.component';
import {AccessControlService} from '../../security/access-control.service';
import {AppDevtoolsPerformanceComponent} from './app-devtools-performance/app-devtools-performance.component';
import {WjTabStatePersistDirective} from '../../directives/wjtab-state-persist.directive';

@Component({
    selector: 'app-app-developer-tools',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        WjNavModule,
        WjInputModule,
        WjGridModule,
        FormsModule,
        ReactiveFormsModule,
        WjGridFilterModule,
        WjGridSearchModule,
        RouterLink,
        MwcCheckboxControlValueAccessorDirective,
        AdminUserPermissionsComponent,
        AppDevtoolsPerformanceComponent,
        WjTabStatePersistDirective
    ],
    templateUrl: './app-developer-tools.component.html',
    styleUrl: './app-developer-tools.component.scss'
})
export class AppDeveloperToolsComponent implements AfterViewInit {

    protected readonly Wijmo = Wijmo;

    protected readonly ApiConfiguration = ApiConfiguration;
    protected readonly environments = ApiConfiguration.environments;
    protected envApiKt = model<string | null>(null);
    protected envApiPy = model<string | null>(null);

    protected connectionSettingsForm = new FormGroup({
        kt: new FormControl<ApiEnvironment>(null),
        py: new FormControl<ApiEnvironment>(null),
        changeCheckFrequency: new FormControl<number>(1000),
        gzipRequests: new FormControl<boolean>(true),
    });

    protected permissionsForm = new FormGroup({
        customPermissions: new FormControl<boolean>(true),
    });

    // signal to track if the form is dirty (Angular has not added signal() style to FormGroup yet)
    protected readonly connectionSettingsFormIsNotDirty = signal(true);

    protected readonly roles =  AccessControlService.AppRoles;

    private readonly accountFormulasGrid = viewChild.required<WjFlexGrid>('accountFormulasGrid');

    protected readonly pfData = inject(PortfolioDataContext);
    private readonly pfSegData = inject(PortfolioSegmentDataContext);

    protected readonly devToolsSvc = inject(AppDevToolsService);
    private readonly dynCompSvc = inject(NgDynamicComponentsService);
    private readonly accessControlSvc = inject(AccessControlService);

    protected permissions = signal<AppRoleAndPermissions>(new AppRoleAndPermissions(['devops']));
    private userPermissionsComponent = viewChild(AdminUserPermissionsComponent)

    constructor() {
        const permissionHeaderValue = this.devToolsSvc.rolePermissions();
        const permissions = permissionHeaderValue ? AppRoleAndPermissions.fromHeaderValue(permissionHeaderValue) : new AppRoleAndPermissions(['devops']);
        this.permissions.set(permissions);
    }

    ngAfterViewInit(): void {
        // Initialize the API environment form with the current values from the dev tools service
        this.connectionSettingsForm.setValue({
            py: ApiConfiguration.envApiPy(),
            kt: ApiConfiguration. envApiKt(),
            changeCheckFrequency: AppConstants.getChangeDetectionFrequency(),
            gzipRequests: AppConstants.getGzipRequests()
        });
        this.connectionSettingsForm.valueChanges.subscribe(status => {
            this.connectionSettingsFormIsNotDirty.set(!this.connectionSettingsForm.dirty)
        });
        this.connectionSettingsForm.markAsUntouched();

        this.permissionsForm.setValue({
            customPermissions: AppConstants.customPermissions(),
        });

        this.permissionsForm.valueChanges.subscribe(this.onCustomPermissionsChanged.bind(this));
    }

    onApplyConnectionSettings() {
        const changeCheckFreq = this.connectionSettingsForm.value.changeCheckFrequency;
        const gzipRequests = this.connectionSettingsForm.value.gzipRequests;

        AppConstants.setChangeDetectionFrequency(changeCheckFreq);
        AppConstants.setGzipRequests(gzipRequests);

        this.devToolsSvc.applyApiEnvKt(this.connectionSettingsForm.value.kt ?? null);
        this.devToolsSvc.applyApiEnvPy(this.connectionSettingsForm.value.py ?? null);
        this.pfSegData.setAccountUpdatesCheckInBackgroundFrequency(AppConstants.getChangeDetectionFrequency());
        this.connectionSettingsForm.markAsUntouched();
        this.connectionSettingsFormIsNotDirty.set(true);
    }

    onConnectToDefaultEnvironments() {
        this.connectionSettingsForm.value.kt = null;// ({ kt: null, py: null });
        this.connectionSettingsFormIsNotDirty.set(false);
    }

    onApplyAppRolePermissions(permissions: AppRoleAndPermissions) {
        const customPermissions = this.permissionsForm.value.customPermissions;
        AppConstants.setUseCustomPermissions(customPermissions);

        if (customPermissions) {
            this.devToolsSvc.applyRolePermissions(permissions);
        } else {
            this.devToolsSvc.applyRolePermissions(null);
        }

        // reapply the security / access rules to the portfolio segments
        this.pfData.applySegmentAccessRules(this.pfData.segments.value());
    }

    onAccountFormulasGridInit(grid: WjFlexGrid) {
        WjGridUtils.addColumns(grid, DevToolsFormulas.PyAccountFormulaColumns);
        const kpiAttrs = this.pfSegData.segmentKpis.value()?.map(it => it.attribute) ?? [];
        grid.itemsSource = DevToolsFormulas.generateAccountFormulaGridData(kpiAttrs);
    }

    protected async generateAccountJson() {
        await DevToolsFormulas.generateAccountJson(this.accountFormulasGrid(), this.pfSegData, this.dynCompSvc);
    }

    protected async generateAccountPython() {
        await DevToolsFormulas.generateAccountPython(this.accountFormulasGrid(), this.pfSegData, this.dynCompSvc);
    }

    protected async onRunChangeDetection() {
        this.pfSegData.checkAccountUpdatesInBackground();
    }

    protected onCustomPermissionsChanged(form: Partial<typeof this.permissionsForm.value>) {
        this.userPermissionsComponent()?.markDirty();
    }
}
